import { Button, Select, Switch, CloseButton, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import type { AssertionPart } from '../converters';
import type { BaseAssertion } from './AssertionPart';
import { assertionsToDisplay } from './AssertionPart';
import { capFirst } from '~/common/string/capitalize';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { z } from 'zod';

export default function AssertionConfig({
  assertionPart: { assertion, invert },
  onClose,
  onSave,
  isSaving,
  readonly,
}: {
  assertionPart: AssertionPart;
  onClose: () => void;
  onSave: (assertion: AssertionPart) => void;
  isSaving: boolean;
  readonly?: boolean;
}) {
  const form = useForm({
    initialValues: {
      assertion,
      invert,
    },
    validate: zod4Resolver(
      z
        .object({
          assertion: z.enum(Object.keys(assertionsToDisplay)),
          invert: z.boolean(),
        })
        .partial(),
    ),
  });

  return (
    <div className="flex flex-col">
      <div className="flex justify-end">
        <CloseButton
          variant="transparent"
          size="md"
          onClick={onClose}
        />
      </div>
      <form
        className="mb-2 flex flex-col gap-6"
        onSubmit={form.onSubmit((values) =>
          onSave({ ...values, assertion: values.assertion as BaseAssertion, type: 'assertion' }),
        )}
      >
        <Select
          label="Assertion Type"
          placeholder="Select assertion type"
          data={Object.entries(assertionsToDisplay).map(([key, value]) => ({
            value: key,
            label: capFirst(value.display),
          }))}
          disabled={readonly}
          searchable
          withAsterisk
          renderOption={({ option }) => (
            <div className="flex flex-nowrap gap-2">
              <div>
                <Text className="text-sm">{option.label}</Text>
                <Text className="text-[11px] text-gray-500">
                  {assertionsToDisplay[option.value as BaseAssertion].description}
                </Text>
              </div>
            </div>
          )}
          {...form.getInputProps('assertion')}
          comboboxProps={{ withinPortal: false }}
        />

        <Switch
          label="Invert assertion (use 'not')"
          description={
            form.values.assertion ?
              `${form.values.invert ? 'NOT ' : ''} ${assertionsToDisplay[form.values.assertion].display}`
            : 'Select an assertion type first'
          }
          disabled={readonly}
          {...form.getInputProps('invert', { type: 'checkbox' })}
        />

        {!readonly && (
          <div className="flex justify-end space-x-2 pt-2">
            <Button
              variant="subtle"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isSaving}
            >
              Save
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
